import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn,
  signOut,
  getCurrentSession,
  onAuthStateChange,
  type AuthUser
} from '../services/authService';
import {
  getAllArticles,
  getArticlesByStatus,
  createArticle,
  updateArticle,
  updateArticleStatus,
  deleteArticle,
  getContentStats
} from '../services/contentService';
import { auditLog } from '../services/analyticsService';
import { isSupabaseConfigured } from '../services/supabase';
import type { Profile, ArticleWithAuthor, ContentStatus } from '../services/database.types';

interface AdminContextType {
  // Auth
  user: AuthUser | null;
  isLoading: boolean;
  isConfigured: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error: string | null }>;
  logout: () => Promise<void>;

  // Content Management
  articles: ArticleWithAuthor[];
  drafts: ArticleWithAuthor[];
  reviewQueue: ArticleWithAuthor[];
  refreshContent: () => Promise<void>;
  saveArticle: (article: Partial<ArticleWithAuthor>) => Promise<{ success: boolean; error: string | null }>;
  changeStatus: (id: string, status: ContentStatus) => Promise<{ success: boolean; error: string | null }>;
  removeArticle: (id: string) => Promise<{ success: boolean; error: string | null }>;

  // Stats
  stats: {
    pendingReviews: number;
    scheduled: number;
    totalPublished: number;
    totalDrafts: number;
  };

  // Audit logging
  logAction: (action: string, details?: string, contentId?: string) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [articles, setArticles] = useState<ArticleWithAuthor[]>([]);
  const [stats, setStats] = useState({
    pendingReviews: 0,
    scheduled: 0,
    totalPublished: 0,
    totalDrafts: 0
  });

  const isConfigured = true; // Local API is always "configured" in this mode

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const session = await getCurrentSession();
        setUser(session);
      } catch (err) {
        console.error('Error initializing auth:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = onAuthStateChange((authUser) => {
      setUser(authUser);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Fetch content when user is authenticated
  const refreshContent = useCallback(async () => {
    if (!user) return;

    try {
      const [allArticles, contentStats] = await Promise.all([
        getAllArticles(),
        getContentStats()
      ]);

      setArticles(allArticles);
      setStats({
        pendingReviews: contentStats.reviewArticles || 0,
        scheduled: 0,
        totalPublished: contentStats.publishedArticles || 0,
        totalDrafts: contentStats.draftArticles || 0
      });
    } catch (err) {
      console.error('Error refreshing content:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshContent();
    }
  }, [user, refreshContent]);

  // Computed values
  const drafts = articles.filter(a => a.status === 'draft');
  const reviewQueue = articles.filter(a => a.status === 'review');

  // Login
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await signIn(email, password);

      if (result.error) {
        return { success: false, error: result.error };
      }

      if (result.user) {
        setUser(result.user);

        // Log the login action
        await auditLog.log(
          'LOGIN',
          result.user.id,
          result.user.name,
          'User logged into admin dashboard'
        );

        return { success: true, error: null };
      }

      return { success: false, error: 'Failed to sign in' };
    } catch (err: any) {
      return { success: false, error: err.message || 'An error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    if (user) {
      await auditLog.log(
        'LOGOUT',
        user.id,
        user.name,
        'User logged out'
      );
    }

    await signOut();
    setUser(null);
    setArticles([]);
  };

  // Save article (create or update)
  const saveArticle = async (articleData: Partial<ArticleWithAuthor>) => {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      if (articleData.id) {
        // Update existing
        const result = await updateArticle(articleData.id, articleData, user.id);
        if (result.error) {
          return { success: false, error: result.error };
        }

        // Log action
        logAction('UPDATE_ARTICLE', `Updated "${articleData.title}"`, articleData.id);

        // Refresh content
        await refreshContent();
        return { success: true, error: null };
      } else {
        // Create new
        const result = await createArticle(articleData, user.id);
        if (result.error) {
          return { success: false, error: result.error };
        }

        // Log action
        logAction('CREATE_ARTICLE', `Created "${articleData.title}"`, result.article?.id);

        // Refresh content
        await refreshContent();
        return { success: true, error: null };
      }
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to save article' };
    }
  };

  // Change article status
  const changeStatus = async (id: string, status: ContentStatus) => {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const result = await updateArticleStatus(id, status, user.id);
      if (result.error) {
        return { success: false, error: result.error };
      }

      // Log action
      logAction('STATUS_CHANGE', `Changed status to ${status}`, id);

      // Refresh content
      await refreshContent();
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update status' };
    }
  };

  // Delete article
  const removeArticle = async (id: string) => {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const result = await deleteArticle(id);
      if (result.error) {
        return { success: false, error: result.error };
      }

      // Log action
      logAction('DELETE_ARTICLE', 'Deleted article', id);

      // Refresh content
      await refreshContent();
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to delete article' };
    }
  };

  // Log action helper
  const logAction = (action: string, details?: string, contentId?: string) => {
    if (!user) return;

    auditLog.log(
      action,
      user.id,
      user.name,
      details,
      contentId,
      'article'
    );
  };

  return (
    <AdminContext.Provider value={{
      user,
      isLoading,
      isConfigured,
      login,
      logout,
      articles,
      drafts,
      reviewQueue,
      refreshContent,
      saveArticle,
      changeStatus,
      removeArticle,
      stats,
      logAction
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};